import React, { useEffect, useMemo, useRef } from 'react';
import { Accordion, Card, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { MultiSelect } from 'react-multi-select-component';
import Loader from '../Utility/Loader/Loader';
import { getAutoIdGenerator, RETIRED_AUTO_ID_COLOUR_GROUPS_TOKEN } from '../helper';
import { AutoToggle } from '../../Pages/SystemMasters/Store/SystemMasters.action';
import './AutoIdGenerator.css';
import CustomTooltip from '../Tooltip/CustomTooltip';
import i18next from 'i18next';

function AutoIdGeneratorTable({
  callApi,
  masterAddEditAccess,
  buttonDown,
  isLoading,
  isDesabled,
  autoIdData,
  onSave,
  state,
  setState,
  brandData,
  customAttributeData,
  multiCustomAttributeData,
}) {
  const { t } = useTranslation('common');
  const reducer = useSelector((estate) => estate);

  const { SystemMastersDetails } = reducer;
  const { productIdentifierList } = SystemMastersDetails;

  const productIdentifierListMemo = useMemo(() => {
    return productIdentifierList?.filter((item) => !['Size', 'Product number', 'Colourway', 'Length']?.includes(item?.name)) || [];
  }, [productIdentifierList]);

  const dispatch = useDispatch();
  const { autoIdModule, productIdentifier } = state;
  const scrollContainerRef = useRef(null);

  /**
   * Helper function to convert string value to object format for dropdown
   * @param {string} value - The string value to convert
   * @param {string} refCode - The reference code to get options from
   * @returns {Object} - Object with label and value
   */
  const convertStringToOption = (value, refCode, customAttributes = [], multiCustomAttributes = []) => {
    const isProductNumberRule = refCode === 'Product_Number' || refCode === 'Product_Number_Universal';
    if (value === 'counter' && isProductNumberRule) {
      return { label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true };
    }

    if (['3', '5'].includes(value) && isProductNumberRule) {
      const options = getAutoIdGenerator[refCode] || [];
      const foundOption = options.find((opt) => opt.value === value);
      if (foundOption) return foundOption;
      return { label: `${value}-digit counter`, value, showInput: true, maxLength: parseInt(value), disabled: false, isCounterField: true };
    }

    if (value === 'counter') {
      return { label: 'Counter', value: 'counter', showInput: true, maxLength: 5 };
    }

    if (value === '-') {
      return { label: '-', value: '-' };
    }

    const options = getAutoIdGenerator[refCode] || [];
    const foundOption = options.find((opt) => opt.value === value);
    if (foundOption) return foundOption;

    const customAttr = customAttributes.find((attr) => attr._id === value);
    if (customAttr) {
      return { label: customAttr.keyName, value: customAttr._id };
    }

    const multiCustomAttr = multiCustomAttributes.find((attr) => attr._id === value);
    if (multiCustomAttr) {
      return { label: multiCustomAttr.keyName, value: multiCustomAttr._id };
    }

    if (value === RETIRED_AUTO_ID_COLOUR_GROUPS_TOKEN) {
      return { label: i18next.t('common:COMMON_COLORWAY_ADD.COLOR_ARTWORK_GROUPS'), value };
    }

    return { label: value, value };
  };

  /**
   * Main useEffect to handle Product Number initialization with edit mode support
   */
  useEffect(() => {
    if (!brandData?.length && !autoIdData?.length) return;

    const autoIdBrandGeneratorData = [];

    const savedUniversal = autoIdData?.find((item) => item.refCode === 'Product_Number_Universal');

    const universalChildProperties = [
      ...(getAutoIdGenerator.Product_Number_Universal || []),
      ...(customAttributeData?.map((attr) => ({ label: attr.keyName, value: attr._id })) || []),
      ...(multiCustomAttributeData?.map((attr) => ({ label: attr.keyName, value: attr._id })) || []),
    ];

    if (savedUniversal) {
      const universalObj = {
        _id: savedUniversal._id,
        name: savedUniversal.name || 'Product Number Universal',
        sequence: 1,
        refCode: 'Product_Number_Universal',
        isActive: savedUniversal.isActive !== undefined ? savedUniversal.isActive : true,
        childProperties: universalChildProperties,
        isModuleActive: savedUniversal.isModuleActive || false,
        brandId: null,
        counterNo3: savedUniversal.counterNo3 || '000',
        counterNo5: savedUniversal.counterNo5 || savedUniversal.counterNo || '00000',
        counterSet3: savedUniversal.counterSet3 !== undefined ? savedUniversal.counterSet3 : false,
        counterSet5: savedUniversal.counterSet5 !== undefined ? savedUniversal.counterSet5 : savedUniversal.counterSet || false,
      };

      if (savedUniversal.selectedChildProperties?.length) {
        const mappedProps = savedUniversal.selectedChildProperties.map((value) =>
          convertStringToOption(value, 'Product_Number_Universal', customAttributeData, multiCustomAttributeData),
        );
        const isCounter = (item) => item?.isCounterField === true || ['3', '5'].includes(item?.value);
        const existingCounters = mappedProps.filter(isCounter);
        const finalCounter =
          existingCounters.length > 0
            ? existingCounters[0]
            : { label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true };
        let counterKept = false;
        universalObj.selectedChildProperties = mappedProps.filter((item) => {
          if (!isCounter(item)) return true;
          if (!counterKept && item?.value === finalCounter?.value) {
            counterKept = true;
            return true;
          }
          return false;
        });
        if (!counterKept) {
          universalObj.selectedChildProperties.push(finalCounter);
        }

        const selectedCounterType = finalCounter?.value;
        if (selectedCounterType === '3') {
          universalObj.counterNo = universalObj.counterNo3;
          universalObj.counterSet = universalObj.counterSet3;
        } else {
          universalObj.counterNo = universalObj.counterNo5;
          universalObj.counterSet = universalObj.counterSet5;
        }
      } else {
        universalObj.selectedChildProperties = [{ label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true }];
        universalObj.counterNo = universalObj.counterNo5;
        universalObj.counterSet = universalObj.counterSet5;
      }

      autoIdBrandGeneratorData.push(universalObj);
    } else {
      const universalObj = {
        name: 'Product Number (Universal)',
        sequence: 1,
        refCode: 'Product_Number_Universal',
        isActive: true,
        childProperties: universalChildProperties,
        isModuleActive: false,
        brandId: null,
        selectedChildProperties: [{ label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true }],
        counterNo3: '000',
        counterNo5: '00000',
        counterSet3: false,
        counterSet5: false,
        counterNo: '00000',
        counterSet: false,
      };

      autoIdBrandGeneratorData.push(universalObj);
    }

    if (brandData?.length) {
      brandData.forEach((brand, index) => {
        let savedBrandData = autoIdData?.find((item) => item.refCode === 'Product_Number' && item.brandId && item.brandId.toString() === brand._id.toString());

        if (!savedBrandData) {
          const expectedName = `Product Number (${brand.name})`;
          savedBrandData = autoIdData?.find(
            (item) =>
              item.refCode === 'Product_Number' &&
              item.name &&
              item.name.toLowerCase().trim() === expectedName.toLowerCase().trim(),
          );
        }

        const brandChildProperties = [
          ...(getAutoIdGenerator.Product_Number || []),
          ...(customAttributeData?.map((attr) => ({ label: attr.keyName, value: attr._id })) || []),
          ...(multiCustomAttributeData?.map((attr) => ({ label: attr.keyName, value: attr._id })) || []),
        ];

        if (savedBrandData) {
          const brandObj = {
            _id: savedBrandData._id,
            name: savedBrandData.name || `Product Number (${brand.name})`,
            sequence: index + 2,
            refCode: 'Product_Number',
            isActive: savedBrandData.isActive !== undefined ? savedBrandData.isActive : true,
            childProperties: brandChildProperties,
            isModuleActive: savedBrandData.isModuleActive || false,
            brandId: brand._id,
            counterNo3: savedBrandData.counterNo3 || '000',
            counterNo5: savedBrandData.counterNo5 || savedBrandData.counterNo || '00000',
            counterSet3: savedBrandData.counterSet3 !== undefined ? savedBrandData.counterSet3 : false,
            counterSet5: savedBrandData.counterSet5 !== undefined ? savedBrandData.counterSet5 : savedBrandData.counterSet || false,
          };

          if (savedBrandData.selectedChildProperties?.length) {
            const mappedProps = savedBrandData.selectedChildProperties.map((value) =>
              convertStringToOption(value, 'Product_Number', customAttributeData, multiCustomAttributeData),
            );
            const isCounter = (item) => item?.isCounterField === true || ['3', '5'].includes(item?.value);
            const existingCounters = mappedProps.filter(isCounter);
            const finalCounter =
              existingCounters.length > 0
                ? existingCounters[0]
                : { label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true };
            let counterKept = false;
            brandObj.selectedChildProperties = mappedProps.filter((item) => {
              if (!isCounter(item)) return true;
              if (!counterKept && item?.value === finalCounter?.value) {
                counterKept = true;
                return true;
              }
              return false;
            });
            if (!counterKept) {
              brandObj.selectedChildProperties.push(finalCounter);
            }

            const selectedCounterType = finalCounter?.value;
            if (selectedCounterType === '3') {
              brandObj.counterNo = brandObj.counterNo3;
              brandObj.counterSet = brandObj.counterSet3;
            } else {
              brandObj.counterNo = brandObj.counterNo5;
              brandObj.counterSet = brandObj.counterSet5;
            }
          } else {
            brandObj.selectedChildProperties = [{ label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true }];
            brandObj.counterNo = brandObj.counterNo5;
            brandObj.counterSet = brandObj.counterSet5;
          }

          autoIdBrandGeneratorData.push(brandObj);
        } else {
          const brandObj = {
            name: `Product Number (${brand.name})`,
            sequence: index + 2,
            refCode: 'Product_Number',
            isActive: true,
            childProperties: brandChildProperties,
            isModuleActive: false,
            brandId: brand._id,
            selectedChildProperties: [{ label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true }],
            counterNo3: '000',
            counterNo5: '00000',
            counterSet3: false,
            counterSet5: false,
            counterNo: '00000',
            counterSet: false,
          };

          autoIdBrandGeneratorData.push(brandObj);
        }
      });
    }

    setState((prev) => ({ ...prev, autoIdModule: autoIdBrandGeneratorData }));
  }, [brandData, autoIdData, customAttributeData, multiCustomAttributeData]);

  /**
   * UseEffect to handle Product Identifier initialization with edit mode support
   */
  useEffect(() => {
    if (!productIdentifierListMemo?.length) return;

    const autoIdProductIdentifierGeneratorData = [];

    productIdentifierListMemo?.forEach((identifier, index) => {
      let savedIdentifierData = autoIdData?.find(
        (item) => item.refCode === 'Product_Identifier' && item.productIdentifierId && item.productIdentifierId.toString() === identifier._id.toString(),
      );

      if (!savedIdentifierData) {
        const expectedName = `Product Identifier ${identifier.name}`;
        savedIdentifierData = autoIdData?.find(
          (item) => item.refCode === 'Product_Identifier' && item.name && item.name.toLowerCase().trim() === expectedName.toLowerCase().trim(),
        );
      }

      if (savedIdentifierData) {
        const identifierObj = {
          _id: savedIdentifierData._id,
          name: savedIdentifierData.name || `Product Identifier ${identifier.name}`,
          sequence: index + 1,
          refCode: 'Product_Identifier',
          isActive: savedIdentifierData.isActive !== undefined ? savedIdentifierData.isActive : true,
          childProperties: [...(getAutoIdGenerator.Product_Identifier || [])],
          isModuleActive: savedIdentifierData.isModuleActive || false,
          brandId: null,
          productIdentifierId: identifier._id,
          isEditable: true,
          counterNo: savedIdentifierData.counterNo || '',
          counterSet: savedIdentifierData.counterSet || false,
          initialCounterValues: savedIdentifierData.initialCounterValues || {},
          latestCounterValues: savedIdentifierData.latestCounterValues || {},
        };

        if (savedIdentifierData.selectedChildProperties?.length) {
          identifierObj.selectedChildProperties = savedIdentifierData.selectedChildProperties.map((value) => {
            if (['3', '5', '10', '11', '12', '13'].includes(value)) {
              const counterOption = identifierObj.childProperties.find((opt) => opt.value === value);
              return counterOption || { label: value, value, isCounterField: true, maxLength: parseInt(value) };
            }
            return convertStringToOption(value, 'Product_Identifier');
          });

          const originalCounter = savedIdentifierData.selectedChildProperties.find((value) => ['3', '5', '10', '11', '12', '13'].includes(value));
          identifierObj.originalCounterValue = originalCounter || null;
        } else {
          identifierObj.selectedChildProperties = [];
          identifierObj.originalCounterValue = null;
        }

        autoIdProductIdentifierGeneratorData.push(identifierObj);
      } else {
        const identifierObj = {
          name: `Product Identifier ${identifier.name}`,
          sequence: index + 1,
          refCode: 'Product_Identifier',
          isActive: true,
          childProperties: [...(getAutoIdGenerator.Product_Identifier || [])],
          isModuleActive: false,
          brandId: null,
          productIdentifierId: identifier._id,
          isEditable: true,
          selectedChildProperties: [],
          counterNo: '',
          counterSet: false,
          originalCounterValue: null,
        };

        autoIdProductIdentifierGeneratorData.push(identifierObj);
      }
    });

    setState((prev) => ({ ...prev, productIdentifier: autoIdProductIdentifierGeneratorData }));
  }, [productIdentifierListMemo, autoIdData]);

  /**
   * UseEffect to prevent PerfectScrollbar from capturing dropdown scroll events
   */
  useEffect(() => {
    const handleDropdownScroll = (e) => {
      if (e?.target?.classList?.contains('dropdown-content') || e?.target?.closest('.dropdown-content')) {
        e.stopPropagation();
      }
    };

    const scrollContainer = scrollContainerRef?.current;
    if (scrollContainer) {
      const psElement = scrollContainer?._container || scrollContainer;
      if (psElement) {
        psElement.addEventListener('wheel', handleDropdownScroll, { passive: false, capture: true });

        return () => {
          psElement.removeEventListener('wheel', handleDropdownScroll, { capture: true });
        };
      }
    }
  }, [scrollContainerRef?.current]);

  const handleToggle = async (val, index, refCode, isProductIdentifier = false) => {
    const updatedStates = isProductIdentifier ? [...productIdentifier] : [...autoIdModule];
    const item = updatedStates[index];
    const itemsToToggleOff = [];

    if (!isProductIdentifier && val) {
      if (refCode === 'Product_Number_Universal') {
        updatedStates.forEach((moduleItem) => {
          if (moduleItem?.refCode === 'Product_Number' && moduleItem.isModuleActive) {
            moduleItem.isModuleActive = false;
            if (moduleItem._id) {
              itemsToToggleOff.push(moduleItem);
            }
          }
        });
      } else if (refCode === 'Product_Number') {
        updatedStates.forEach((moduleItem) => {
          if (moduleItem?.refCode === 'Product_Number_Universal' && moduleItem.isModuleActive) {
            moduleItem.isModuleActive = false;
            if (moduleItem._id) {
              itemsToToggleOff.push(moduleItem);
            }
          }
        });
      }
    }

    if (!item?._id) {
      item.isModuleActive = val;
      if (isProductIdentifier) {
        setState((prev) => ({ ...prev, productIdentifier: updatedStates }));
      } else {
        setState((prev) => ({ ...prev, autoIdModule: updatedStates, isProductNumberEdit: true }));
      }
      return;
    }

    try {
      const toggleOffPromises = itemsToToggleOff.map((itemToToggleOff) =>
        dispatch(
          AutoToggle(
            {
              isModuleActive: false,
              id: itemToToggleOff._id,
              name: itemToToggleOff.name,
            },
            t,
          ),
        ),
      );

      const requestData = {
        isModuleActive: val,
        id: item._id,
        name: item.name,
      };

      const results = await Promise.all([dispatch(AutoToggle(requestData, t)), ...toggleOffPromises]);

      if (results[0]) {
        item.isModuleActive = val;

        if (isProductIdentifier) {
          setState((prev) => ({ ...prev, productIdentifier: updatedStates }));
        } else {
          setState((prev) => ({ ...prev, autoIdModule: updatedStates, isProductNumberEdit: true }));
        }
      }
    } catch (error) {
      console.error('Error toggling module:', error);
    }
  };

  const incrementedDash = () => {
    return { label: '-', value: '-' };
  };

  const onSelectionChange = (val, index, counterNo) => {
    const updatedToggleStates = [...autoIdModule];
    const currentSelected = updatedToggleStates[index].selectedChildProperties || [];

    const isCounter = (item) => item?.isCounterField === true || ['3', '5'].includes(item?.value);

    const currentCounters = currentSelected.filter((item) => isCounter(item));
    const newCounters = val.filter((item) => isCounter(item));

    const wasDeselecting = currentCounters.length > 0 && newCounters.length === 0;

    let finalCounter = null;
    if (newCounters.length > 0) {
      finalCounter = newCounters[newCounters.length - 1];
    } else if (!wasDeselecting) {
      finalCounter = { label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true };
      if (!updatedToggleStates[index].counterNo3) {
        updatedToggleStates[index].counterNo3 = '000';
      }
      if (!updatedToggleStates[index].counterNo5) {
        updatedToggleStates[index].counterNo5 = updatedToggleStates[index].counterNo || '00000';
      }
      if (updatedToggleStates[index].counterSet3 === undefined) {
        updatedToggleStates[index].counterSet3 = false;
      }
      if (updatedToggleStates[index].counterSet5 === undefined) {
        updatedToggleStates[index].counterSet5 = updatedToggleStates[index].counterSet || false;
      }
    }

    const newRegularItems = val.filter((item) => !isCounter(item) && item?.value !== '-');
    const newRegularValues = new Set(newRegularItems.map((item) => item?.value));

    const rebuiltArray = [];
    const usedItems = new Set();

    currentSelected.forEach((item) => {
      if (item?.value === '-') {
        rebuiltArray.push(item);
      } else if (isCounter(item)) {
        if (finalCounter) {
          rebuiltArray.push(finalCounter);
          usedItems.add('counter');
        }
      } else if (newRegularValues.has(item?.value)) {
        const itemFromVal = newRegularItems.find((valItem) => valItem?.value === item?.value);
        rebuiltArray.push(itemFromVal || item);
        usedItems.add(item?.value);
      }
    });

    newRegularItems.forEach((item) => {
      if (!usedItems.has(item?.value)) {
        rebuiltArray.push(item);
        usedItems.add(item?.value);
      }
    });

    if (finalCounter && !usedItems.has('counter')) {
      rebuiltArray.push(finalCounter);
    }

    if (finalCounter) {
      const counterType = finalCounter?.value;
      const counterMaxLength = finalCounter?.maxLength || parseInt(counterType) || 5;

      if (!updatedToggleStates[index].counterNo3) {
        updatedToggleStates[index].counterNo3 = '000';
      }
      if (!updatedToggleStates[index].counterNo5) {
        updatedToggleStates[index].counterNo5 = updatedToggleStates[index].counterNo || '00000';
      }
      if (updatedToggleStates[index].counterSet3 === undefined) {
        updatedToggleStates[index].counterSet3 = false;
      }
      if (updatedToggleStates[index].counterSet5 === undefined) {
        updatedToggleStates[index].counterSet5 = updatedToggleStates[index].counterSet || false;
      }

      if (counterType === '3') {
        if (finalCounter?.value !== currentCounters[0]?.value) {
          updatedToggleStates[index].counterNo3 = updatedToggleStates[index].counterNo3 || '000';
        }
        updatedToggleStates[index].counterNo = updatedToggleStates[index].counterNo3;
        updatedToggleStates[index].counterSet = updatedToggleStates[index].counterSet3;
      } else if (counterType === '5') {
        if (finalCounter?.value !== currentCounters[0]?.value) {
          updatedToggleStates[index].counterNo5 = updatedToggleStates[index].counterNo5 || '00000';
        }
        updatedToggleStates[index].counterNo = updatedToggleStates[index].counterNo5;
        updatedToggleStates[index].counterSet = updatedToggleStates[index].counterSet5;
      }
    } else {
      updatedToggleStates[index].counterNo = '';
    }

    updatedToggleStates[index].selectedChildProperties = rebuiltArray;

    if (counterNo) {
      const selectedCounter = rebuiltArray.find((item) => item?.isCounterField === true || ['3', '5'].includes(item?.value));
      if (selectedCounter?.value === '3') {
        updatedToggleStates[index].counterNo3 = val;
        updatedToggleStates[index].counterNo = val;
      } else if (selectedCounter?.value === '5') {
        updatedToggleStates[index].counterNo5 = val;
        updatedToggleStates[index].counterNo = val;
      } else {
        updatedToggleStates[index].counterNo = val;
      }
    }
    setState((prev) => ({ ...prev, autoIdModule: updatedToggleStates, isProductNumberEdit: true }));
  };

  const onCounterChange = (val, index, isProductIdentifier = false) => {
    const numericValue = val.replace(/[^0-9]/g, '');

    if (isProductIdentifier) {
      const updatedData = [...productIdentifier];
      updatedData[index].counterNo = numericValue;
      setState((prev) => ({ ...prev, productIdentifier: updatedData }));
    } else {
      const updatedToggleStates = [...autoIdModule];
      const selectedCounter = updatedToggleStates[index]?.selectedChildProperties?.find(
        (item) => item?.isCounterField === true || ['3', '5'].includes(item?.value),
      );

      if (selectedCounter?.value === '3') {
        updatedToggleStates[index].counterNo3 = numericValue;
        updatedToggleStates[index].counterNo = numericValue;
      } else if (selectedCounter?.value === '5') {
        updatedToggleStates[index].counterNo5 = numericValue;
        updatedToggleStates[index].counterNo = numericValue;
      } else {
        updatedToggleStates[index].counterNo = numericValue;
      }
      setState((prev) => ({ ...prev, autoIdModule: updatedToggleStates, isProductNumberEdit: true }));
    }
  };

  const hasRestrictedCounter = (index) => {
    if (index === undefined || index === null) return false;
    const item = productIdentifier[index];
    if (!item) return false;
    const selectedCounter = item?.selectedChildProperties?.find((prop) => ['11', '12', '13'].includes(prop?.value));
    return selectedCounter !== undefined;
  };

  const onSelectionProductidentiferNumber = (val, index) => {
    const updatedData = [...productIdentifier];
    const currentSelected = updatedData[index]?.selectedChildProperties || [];

    const isCounter = (item) => item?.isCounterField === true || ['3', '5', '10', '11', '12', '13'].includes(item?.value);

    const currentCounter = currentSelected.find((item) => isCounter(item));
    const newCounter = val.find((item) => isCounter(item));

    const isRestrictedCounter = newCounter && ['11', '12', '13'].includes(newCounter?.value);

    if (isRestrictedCounter) {
      updatedData.forEach((row, idx) => {
        if (idx === index) {
          row.selectedChildProperties = [newCounter];
          const counterMaxLength = newCounter?.maxLength || parseInt(newCounter?.value) || 10;
          row.counterNo = row?.initialCounterValues?.[newCounter?.value] || '0'.repeat(counterMaxLength);
          const hasOnlyZeros = row?.latestCounterValues?.[newCounter?.value]
            ? row.latestCounterValues[newCounter.value] === '0'.repeat(row.latestCounterValues[newCounter.value].length)
            : true;
          row.counterSet = !hasOnlyZeros;
        } else {
          const hasRestrictedCounter = row.selectedChildProperties?.some((prop) => ['11', '12', '13'].includes(prop?.value));
          if (hasRestrictedCounter) {
            row.selectedChildProperties = [];
            row.counterNo = '';
            row.counterSet = false;
          }
        }
      });
      setState((prev) => ({ ...prev, productIdentifier: updatedData }));
      return;
    }

    const newRegularItems = val.filter((item) => !isCounter(item) && item?.value !== '-');
    const newRegularValues = new Set(newRegularItems.map((item) => item?.value));

    const resultArray = [];
    const usedItems = new Set();

    currentSelected.forEach((item) => {
      if (item?.value === '-') {
        resultArray.push(item);
      } else if (isCounter(item)) {
        if (newCounter) {
          resultArray.push(newCounter);
          usedItems.add('counter');
        }
      } else if (newRegularValues.has(item?.value)) {
        const itemFromVal = newRegularItems.find((valItem) => valItem?.value === item?.value);
        resultArray.push(itemFromVal || item);
        usedItems.add(item?.value);
      }
    });

    newRegularItems.forEach((item) => {
      if (!usedItems.has(item?.value)) {
        resultArray.push(item);
        usedItems.add(item?.value);
      }
    });

    if (newCounter && !resultArray.some((item) => isCounter(item))) {
      resultArray.push(newCounter);
    }

    if (newCounter && newCounter?.value !== currentCounter?.value) {
      const counterMaxLength = newCounter?.maxLength || parseInt(newCounter?.value) || 10;
      if (updatedData[index]?.initialCounterValues?.[newCounter?.value]) {
        updatedData[index].counterNo = updatedData[index].initialCounterValues[newCounter.value];
      } else {
        updatedData[index].counterNo = '0'.repeat(counterMaxLength);
      }

      const hasOnlyZeros = updatedData[index]?.latestCounterValues?.[newCounter?.value]
        ? updatedData[index].latestCounterValues[newCounter.value] === '0'.repeat(updatedData[index].latestCounterValues[newCounter.value].length)
        : true;
      updatedData[index].counterSet = !hasOnlyZeros;
    } else if (!newCounter) {
      updatedData[index].counterNo = '';
      updatedData[index].counterSet = false;
    }

    updatedData[index].selectedChildProperties = resultArray;
    setState((prev) => ({ ...prev, productIdentifier: updatedData }));
  };

  const onDashButtonClick = (index) => {
    const updatedToggleStates = [...autoIdModule];
    if (!updatedToggleStates[index]) return;
    const newDash = incrementedDash();

    updatedToggleStates[index].selectedChildProperties = updatedToggleStates[index].selectedChildProperties || [];

    const selectedProps = updatedToggleStates[index].selectedChildProperties;

    const rebuiltArray = [...selectedProps, newDash];

    updatedToggleStates[index].selectedChildProperties = rebuiltArray;

    setState((prev) => ({ ...prev, autoIdModule: updatedToggleStates, isProductNumberEdit: true }));
  };

  const onDashButton = (idx) => {
    const updatedData = productIdentifier.map((e, index) => {
      if (idx === index) {
        const newDash = incrementedDash();

        e.selectedChildProperties = e.selectedChildProperties || [];

        e.selectedChildProperties.push(newDash);
      }
      return e;
    });
    setState((prev) => ({ ...prev, productIdentifier: updatedData }));
  };

  /**
   * Handles removing a tag from selected items (Product Number)
   * @param {Object} item - The item to remove
   * @param {number} index - The index of the autoIdModule item
   * @param {number} itemIndex - The specific index of the item in the array (for dash removal)
   */
  const handleRemoveTag = (item, index, itemIndex) => {
    const isCounterItem = item?.isCounterField === true || ['3', '5'].includes(item?.value);

    if (isCounterItem) {
      const updatedToggleStates = [...autoIdModule];
      const currentSelected = updatedToggleStates[index]?.selectedChildProperties || [];
      const newSelected = currentSelected.filter((_, idx) => idx !== itemIndex);

      const hasCounter = newSelected.some((sel) => sel?.isCounterField === true || ['3', '5'].includes(sel?.value));

      if (hasCounter) {
        const remainingCounter = newSelected.find((sel) => sel?.isCounterField === true || ['3', '5'].includes(sel?.value));
        if (remainingCounter?.value === '3') {
          updatedToggleStates[index].counterNo = updatedToggleStates[index].counterNo3 || '000';
          updatedToggleStates[index].counterSet = updatedToggleStates[index].counterSet3 !== undefined ? updatedToggleStates[index].counterSet3 : false;
        } else if (remainingCounter?.value === '5') {
          updatedToggleStates[index].counterNo = updatedToggleStates[index].counterNo5 || '00000';
          updatedToggleStates[index].counterSet = updatedToggleStates[index].counterSet5 !== undefined ? updatedToggleStates[index].counterSet5 : false;
        }
      } else {
        updatedToggleStates[index].counterNo = '';
        updatedToggleStates[index].counterSet = false;
      }

      updatedToggleStates[index].selectedChildProperties = newSelected;
      setState((prev) => ({ ...prev, autoIdModule: updatedToggleStates, isProductNumberEdit: true }));
      return;
    }

    const updatedToggleStates = [...autoIdModule];
    const currentSelected = updatedToggleStates[index]?.selectedChildProperties || [];

    if (item?.value === '-') {
      const newSelected = currentSelected.filter((_, idx) => idx !== itemIndex);
      updatedToggleStates[index].selectedChildProperties = newSelected;
    } else {
      let removed = false;
      const newSelected = currentSelected?.filter((selectedItem) => {
        if (!removed && selectedItem?.value === item?.value) {
          removed = true;
          return false;
        }
        return true;
      });
      updatedToggleStates[index].selectedChildProperties = newSelected;
    }

    setState((prev) => ({ ...prev, autoIdModule: updatedToggleStates, isProductNumberEdit: true }));
  };

  /**
   * Handles removing a tag from selected items (Product Identifier)
   * @param {Object} item - The item to remove
   * @param {number} index - The index of the productIdentifier item
   * @param {number} itemIndex - The specific index of the item in the array (for dash removal)
   */
  const handleRemoveTagProductIdentifier = (item, index, itemIndex) => {
    const isCounterItem = item?.isCounterField === true || ['3', '5', '10', '11', '12', '13'].includes(item?.value);

    const isRestrictedCounter = isCounterItem && ['11', '12', '13'].includes(item?.value);

    if (isRestrictedCounter) {
      const updatedData = [...productIdentifier];
      const currentSelected = updatedData[index]?.selectedChildProperties || [];
      const newSelected = currentSelected.filter((_, idx) => idx !== itemIndex);
      updatedData[index].selectedChildProperties = newSelected;
      updatedData[index].counterNo = '';
      updatedData[index].counterSet = false;
      setState((prev) => ({ ...prev, productIdentifier: updatedData }));
      return;
    }

    if (isCounterItem && ['3', '5', '10'].includes(item?.value)) {
      const updatedData = [...productIdentifier];
      const currentSelected = updatedData[index]?.selectedChildProperties || [];
      const newSelected = currentSelected.filter((_, idx) => idx !== itemIndex);
      updatedData[index].selectedChildProperties = newSelected;
      updatedData[index].counterNo = '';
      updatedData[index].counterSet = false;
      setState((prev) => ({ ...prev, productIdentifier: updatedData }));
      return;
    }

    if (isCounterItem) {
      return;
    }

    const updatedData = [...productIdentifier];
    const currentSelected = updatedData[index]?.selectedChildProperties || [];

    if (item?.value === '-') {
      const newSelected = currentSelected.filter((_, idx) => idx !== itemIndex);
      updatedData[index].selectedChildProperties = newSelected;
    } else {
      let removed = false;
      const newSelected = currentSelected?.filter((selectedItem) => {
        if (!removed && selectedItem?.value === item?.value) {
          removed = true;
          return false;
        }
        return true;
      });
      updatedData[index].selectedChildProperties = newSelected;
    }

    setState((prev) => ({ ...prev, productIdentifier: updatedData }));
  };

  const valueRenderer = (selected, index, autoIdItem) => {
    const isCounterItem = (item) => item?.isCounterField === true || ['3', '5'].includes(item?.value);

    return (
      <div className="auto-id-tags-container">
        {selected?.map((item, itemIndex) => {
          if (isCounterItem(item)) {
            const counterType = item?.value;
            let counterValue = '';
            let counterSetValue = false;

            if (counterType === '3') {
              counterValue = autoIdItem?.counterNo3 || autoIdItem?.counterNo || '';
              counterSetValue = autoIdItem?.counterSet3 !== undefined ? autoIdItem.counterSet3 : autoIdItem?.counterSet || false;
            } else if (counterType === '5') {
              counterValue = autoIdItem?.counterNo5 || autoIdItem?.counterNo || '';
              counterSetValue = autoIdItem?.counterSet5 !== undefined ? autoIdItem.counterSet5 : autoIdItem?.counterSet || false;
            } else {
              counterValue = autoIdItem?.counterNo || '';
              counterSetValue = autoIdItem?.counterSet || false;
            }

            return (
              <div key={`${item.value}-${itemIndex}`} className="auto-id-tag-counter">
                <span className="auto-id-tag-label">{item?.label}</span>
                <input
                  type="text"
                  name="counter"
                  className="auto-id-counter-input"
                  maxLength={item?.maxLength || 5}
                  autoComplete="off"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={counterValue}
                  disabled={counterSetValue}
                  onChange={(e) => onCounterChange(e.target.value, index, false)}
                />
                <button
                  type="button"
                  className="auto-id-tag-remove"
                  onClick={() => handleRemoveTag(item, index, itemIndex)}
                  aria-label={`Remove ${item.label}`}
                >
                  ✖
                </button>
              </div>
            );
          }
          if (item.value === '-') {
            return (
              <div key={`dash-${itemIndex}`} className="auto-id-tag auto-id-tag-dash">
                <span className="auto-id-tag-label">-</span>
                <button type="button" className="auto-id-tag-remove" onClick={() => handleRemoveTag(item, index, itemIndex)} aria-label="Remove dash">
                  ✖
                </button>
              </div>
            );
          }

          return (
            <div key={`${item.value}-${itemIndex}`} className="auto-id-tag">
              <span className="auto-id-tag-label">{item.label}</span>
              <button type="button" className="auto-id-tag-remove" onClick={() => handleRemoveTag(item, index, itemIndex)} aria-label={`Remove ${item.label}`}>
                ✖
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const valueRendererProductIdentifier = (selected, index, productIdItem) => {
    const isCounterItem = (item) => item?.isCounterField === true || ['3', '5', '10', '11', '12', '13'].includes(item?.value);
    if (!selected || selected.length === 0) {
      return <span className="gray">Select attributes</span>;
    }

    return (
      <div className="auto-id-tags-container">
        {selected?.map((item, itemIndex) => {
          if (isCounterItem(item)) {
            const isRestrictedCounter = ['11', '12', '13'].includes(item?.value);
            const currentCounterValue = item?.value;
            const originalCounterValue = productIdItem?.originalCounterValue;
            const isCounterUnchanged = originalCounterValue !== null && currentCounterValue === originalCounterValue;

            return (
              <div key={`${item.value}-${itemIndex}`} className="auto-id-tag-counter">
                <span className="auto-id-tag-label">{item?.label}</span>
                <input
                  type="text"
                  name="counter"
                  className="auto-id-counter-input"
                  maxLength={item?.maxLength || 10}
                  autoComplete="off"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={productIdItem?.counterNo || ''}
                  disabled={isCounterUnchanged}
                  onChange={(e) => onCounterChange(e.target.value, index, true)}
                />
                {isRestrictedCounter && (
                  <button
                    type="button"
                    className="auto-id-tag-remove"
                    onClick={() => handleRemoveTagProductIdentifier(item, index, itemIndex)}
                    aria-label={`Remove ${item.label}`}
                  >
                    ✖
                  </button>
                )}
              </div>
            );
          }

          if (item?.value === '-') {
            return (
              <div key={`dash-${itemIndex}`} className="auto-id-tag auto-id-tag-dash">
                <span className="auto-id-tag-label">-</span>
                <button
                  type="button"
                  className="auto-id-tag-remove"
                  onClick={() => handleRemoveTagProductIdentifier(item, index, itemIndex)}
                  aria-label="Remove dash"
                >
                  ✖
                </button>
              </div>
            );
          }

          return (
            <div key={`${item.value}-${itemIndex}`} className="auto-id-tag">
              <span className="auto-id-tag-label">{item?.label}</span>
              <button
                type="button"
                className="auto-id-tag-remove"
                onClick={() => handleRemoveTagProductIdentifier(item, index, itemIndex)}
                aria-label={`Remove ${item.label}`}
              >
                ✖
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Accordion className="mb-4 master-accordian">
      <Card>
        <Card.Header>
          <div className="title-container">
            <p>{t('SYSTEM_MASTER.AUTO_ID_GENERATOR')}</p>
          </div>
          <div className={!masterAddEditAccess ? 'action-title-btns single-btn' : 'action-title-btns'}>
            <OverlayTrigger
              placement="auto"
              delay={{ show: 250, hide: 400 }}
              overlay={<Tooltip>{!buttonDown ? t('SYSTEM_MASTER_TABLE.CLOSE') : t('SYSTEM_MASTER_TABLE.OPEN')}</Tooltip>}
            >
              <button onClick={callApi} type="button">
                {!buttonDown ? <i className="icon-new-up" /> : <i className="icon-new-down" />}
              </button>
            </OverlayTrigger>
          </div>
        </Card.Header>
        {!buttonDown && (
          <Card.Body>
            {isLoading ? (
              <Loader />
            ) : (
              <div className="common-scrollbar">
                <ul>
                  {!isEmpty(autoIdModule) &&
                    autoIdModule?.map(
                      (item, index) =>
                        (item?.refCode === 'Product_Number' || item?.refCode === 'Product_Number_Universal') && (
                          <li key={item?._id} className="">
                            <div className="flex-fill">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                                  <div className="d-inline-flex align-items-center design-switch">
                                    <input
                                      type="checkbox"
                                      className="form-check-input comment-switch-input mt-0"
                                      id={`customSwitch-${index}`}
                                      checked={item?.isModuleActive}
                                      onChange={(e) => handleToggle(e?.target?.checked, index, item?.refCode)}
                                    />
                                    <label className="switch-text font-heavy" htmlFor={`customSwitch-${index}`} />
                                  </div>
                                  <span
                                    className="border-none ml-0 text-truncate"
                                    style={{
                                      display: 'inline-block',
                                      maxWidth: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 1,
                                    }}
                                  >
                                    {item?.name || (typeof item?.name === 'number' ? item?.name : '')}
                                  </span>
                                </div>
                                <div className="input-action">
                                  <button aria-label="Add Dash" type="button" onClick={() => onDashButtonClick(index)} className="btn btn-dash">
                                    <span>-</span>
                                  </button>
                                </div>
                              </div>

                              {item?.isModuleActive && (
                                <div className="common-react-select h-auto mt-1">
                                  <MultiSelect
                                    options={
                                      item?.childProperties
                                        ?.filter((ele) => typeof ele?.value === 'string')
                                        ?.map((opt) => {
                                          const currentCounters = item?.selectedChildProperties?.filter(
                                            (sel) => sel?.isCounterField === true || ['3', '5'].includes(sel?.value),
                                          );
                                          const selectedCounter = currentCounters.length > 0 ? currentCounters[0] : null;
                                          const isCounterOption = opt?.isCounterField === true || ['3', '5'].includes(opt?.value);
                                          let disabled = false;
                                          if (isCounterOption && selectedCounter) {
                                            disabled = selectedCounter?.value !== opt?.value;
                                          }

                                          return {
                                            ...opt,
                                            disabled,
                                          };
                                        }) || []
                                    }
                                    value={item?.selectedChildProperties || []}
                                    onChange={(val) => onSelectionChange(val, index)}
                                    labelledBy="Select attributes"
                                    className="product-custom-multiselect common-new-multiselect w-100"
                                    valueRenderer={(sel) => valueRenderer(sel, index, item)}
                                  />
                                  {item?.err && <span className="text-danger">{item?.err}</span>}
                                </div>
                              )}
                            </div>
                          </li>
                        ),
                    )}
                </ul>
                <ul>
                  {!isEmpty(productIdentifier) &&
                    productIdentifier?.map(
                      (item, index) =>
                        item?.isEditable === true &&
                        item?.isActive === true && (
                          <li key={item?._id} className="">
                            <div className="flex-fill">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex flex-fill align-items-center">
                                  <div className="d-inline-flex align-items-center design-switch">
                                    <input
                                      type="checkbox"
                                      className="form-check-input comment-switch-input mt-0"
                                      id={`customSwitch-${index}`}
                                      checked={item?.isModuleActive}
                                      onChange={(e) => handleToggle(e?.target?.checked, index, item?.refCode, true)}
                                    />
                                    <label aria-label="Product Identifier" className="switch-text font-heavy" htmlFor={`customSwitch-${index}`} />
                                  </div>
                                  <input
                                    className="border-none flex-fill ml-0"
                                    name={item?.name}
                                    value={
                                      typeof item?.name === 'number'
                                        ? item?.name
                                        : item?.name && typeof item?.name === 'string'
                                        ? item?.name.includes('Product Identifier')
                                          ? item?.name
                                          : `Product Identifier ${item?.name}`
                                        : ''
                                    }
                                    type="text"
                                  />
                                </div>
                                <div className="input-action">
                                  <button
                                    aria-label="Add Dash"
                                    type="button"
                                    onClick={() => onDashButton(index)}
                                    className="btn btn-dash"
                                    disabled={hasRestrictedCounter(index)}
                                  >
                                    <span>-</span>
                                  </button>
                                </div>
                              </div>

                              {item?.isModuleActive && (
                                <div className="common-react-select h-auto placement-top mt-1">
                                  <MultiSelect
                                    options={
                                      item?.childProperties
                                        .filter((ele) => typeof ele?.value === 'string')
                                        .map((opt) => {
                                          const isCounterOption = opt?.isCounterField === true || ['3', '5', '10', '11', '12', '13'].includes(opt?.value);

                                          const selectedCounter = item?.selectedChildProperties?.find(
                                            (prop) => prop?.isCounterField === true || ['3', '5', '10', '11', '12', '13'].includes(prop?.value),
                                          );

                                          let disabled = false;

                                          if (selectedCounter) {
                                            const selectedCounterValue = selectedCounter?.value;

                                            if (['3', '5', '10'].includes(selectedCounterValue)) {
                                              if (isCounterOption) {
                                                disabled = opt?.value !== selectedCounterValue;
                                              } else {
                                                disabled = false;
                                              }
                                            }
                                            else if (['11', '12', '13'].includes(selectedCounterValue)) {
                                              if (isCounterOption) {
                                                disabled = opt?.value !== selectedCounterValue;
                                              } else {
                                                disabled = true;
                                              }
                                            }
                                          }

                                          return {
                                            ...opt,
                                            disabled,
                                          };
                                        }) || []
                                    }
                                    value={item?.selectedChildProperties || []}
                                    placeholder="Select attributes"
                                    counterNo={item?.counterNo}
                                    counterSet={item.counterSet}
                                    className="product-custom-multiselect common-new-multiselect w-100"
                                    labelledBy="Select attributes"
                                    valueRenderer={(sel) => valueRendererProductIdentifier(sel, index, item)}
                                    onChange={(val) => onSelectionProductidentiferNumber(val, index)}
                                  />

                                  {item?.err && <span className="text-danger">{item?.err}</span>}
                                </div>
                              )}
                            </div>
                          </li>
                        ),
                    )}
                </ul>
              </div>
            )}

            <div className="section-save">
              <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                <button type="button" className="btn btn-page-filter btn-white" onClick={onSave} aria-label="Save">
                  {isDesabled ? (
                    <Spinner animation="border" />
                  ) : (
                    <>
                      <span className="icon-Save c-button-align f-20 mr-2" />
                      {t('SYSTEM_MASTER_TABLE.SAVE')}
                    </>
                  )}
                </button>
              </CustomTooltip>
            </div>
          </Card.Body>
        )}
      </Card>
    </Accordion>
  );
}

export default AutoIdGeneratorTable;
